/**
 * Quiet Hours Hub — the entry point at `/quiet-hours`.
 *
 * Four category cards drift into place. The header is hushed, almost
 * conversational. A small "Today's Quiet Hour" hint surfaces a single
 * suggested category based on time-of-day — soft suggestion, not a
 * required path.
 *
 * Aesthetic notes:
 *  - No bouncy entrances. Cards drift with a slow soft spring.
 *  - Card hover/focus lifts ~3px with a slow ease — never a jump.
 *  - No haptics, no clicks. The hub is silent until the resident
 *    chooses a category (and even then, only the ambient soundscape
 *    rises slowly).
 */
import { motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
/* <Link> still used by category tiles below — the "Back to all games"
 * affordance moved into the global <BackToGamesBar />. */

import {
  AtmosphereSurface,
  buildSrcSet,
  variantOf,
} from '@/juice/RevealedImage';
import { usePreferences } from '@/preferences';

import {
  CATEGORIES,
  suggestedForHour,
  type QuietHoursCategoryMeta,
} from './categories';
import { driftSpring, FADE, SLOW_STAGGER_S } from './motion';

import './Hub.css';

export function QuietHoursHub() {
  const { theme } = usePreferences();
  const [suggested, setSuggested] = useState<QuietHoursCategoryMeta | null>(
    null,
  );

  // Pick the suggested category once on mount, then settle. We don't
  // re-pick on the hour — that would feel like the screen was "watching."
  useEffect(() => {
    setSuggested(suggestedForHour(new Date().getHours()));
  }, []);

  return (
    <div className="qh-hub" data-theme={theme}>
      <div className="qh-hub__field" aria-hidden="true" />

      <header className="qh-hub__header">
        <motion.h1
          className="qh-hub__title"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={FADE.in}
        >
          Quiet Hours
        </motion.h1>
        <motion.p
          className="qh-hub__subtitle"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...FADE.in, delay: 0.4 }}
        >
          A small space for breathing. Choose what suits this hour.
        </motion.p>
        {suggested && (
          <motion.p
            className="qh-hub__hint"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...FADE.in, delay: 0.9 }}
          >
            {hintFor(suggested)}
          </motion.p>
        )}
      </header>

      <section className="qh-hub__rooms" aria-labelledby="qh-rooms-title">
        <h2 id="qh-rooms-title" className="sr-only">
          Categories
        </h2>
        <ul className="qh-hub__list" role="list">
          {CATEGORIES.map((cat, i) => (
            <motion.li
              key={cat.slug}
              className="qh-hub__item"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...driftSpring, delay: 0.6 + i * SLOW_STAGGER_S }}
            >
              <CategoryCard
                category={cat}
                theme={theme}
                isSuggested={suggested?.slug === cat.slug}
                eager={i < 2}
              />
            </motion.li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function hintFor(cat: QuietHoursCategoryMeta): string {
  switch (cat.slug) {
    case 'morning-calm':
      return "It's morning — Morning Calm is a gentle place to start.";
    case 'evening-wind-down':
      return "It's evening — try Evening Wind-Down to settle the day.";
    case 'anxiety-relief':
      return 'Mid-day moments can feel busy — Anxiety Relief is here when you need a pause.';
    case 'sleep-aid':
      return "It's late — Sleep Aid is a slow path toward rest.";
  }
}

// ---------------------------------------------------------------------------
// CategoryCard
// ---------------------------------------------------------------------------

function CategoryCard({
  category,
  theme,
  isSuggested,
  eager,
}: {
  category: QuietHoursCategoryMeta;
  theme: 'light' | 'dark';
  isSuggested: boolean;
  eager: boolean;
}) {
  const bg = theme === 'dark' ? category.atmosphereDark : category.atmosphereLight;
  return (
    <Link
      to={`/quiet-hours/${category.slug}`}
      className="qh-card"
      data-suggested={isSuggested || undefined}
    >
      <div className="qh-card__field" aria-hidden="true">
        <AtmosphereSurface
          src={bg}
          avifSrc={variantOf(bg, 'avif')}
          webpSrc={variantOf(bg, 'webp')}
          avifSrcSet={buildSrcSet(bg, 'avif', [430, 860, 1366])}
          webpSrcSet={buildSrcSet(bg, 'webp', [430, 860, 1366])}
          sizes="(max-width: 430px) 100vw, (max-width: 1024px) 50vw, 25vw"
          eager={eager}
        />
      </div>
      <div className="qh-card__scrim" aria-hidden="true" />
      <div className="qh-card__body">
        {isSuggested && (
          <span className="qh-card__pill" aria-label="Suggested for now">
            For now
          </span>
        )}
        <h3 className="qh-card__title">{category.title}</h3>
        <p className="qh-card__blurb">{category.blurb}</p>
      </div>
    </Link>
  );
}
